import useSWR from 'swr';
import {
    ParkingLot,
    PublicApiCity,
    ParkingLotMapItem,
    PublicApiCitiesResponse,
    PublicApiParkingLotsResponse,
    PublicApiParkingLotDetailResponse,
    ParkingLotStats,
    DashboardOverview,
    ActionHeatmapAction,
    ActionHeatmapResponse,
    FeedbackSummary,
    FeedbackActivityResponse,
    LiveDataControlsResponse,
    ToggleLotResponse,
    UpdateMetricsResponse,
    VersionResponse,
    ForceVersionUpdateResponse,
    SessionsResponse,
    ReportResponse,
    ReceiptRequest,
    ReceiptRequestsResponse,
    ReceiptRequestFilters,
    Campaign,
    CampaignsResponse,
    CampaignResponse,
    EligibleSessionsResponse,
    MarkRefundedRequest,
    MarkRefundedResponse,
    CampaignRefund
} from './types';
import { mockDashboardData, mockParkingLots, mockParkingLotStats, generatePaginatedParkingLots } from './mockData';
import type {
    OverviewResponse,
    NewUsersBreakdownResponse,
    TimeseriesResponse,
    TimeseriesMetric,
    PaymentFunnelResponse,
    SearchFunnelResponse,
    RemindersResponse,
    TopLotsResponse,
    TopLotsSource,
    PanelEventsResponse,
    PanelEventSource,
    EventNamesResponse,
    WebOverviewResponse,
    WebAcquisitionResponse,
    WebCityEngagementResponse,
    WebTimeseriesResponse,
    WebTimeseriesMetric,
} from './panelTypes';
import environment from '../config/environment';
import type { ParkingLotsEditorFiltersState } from './parkingLotsEditorFilters';
import {
    appendEditorFiltersToSearchParams,
    mapContextSecondaryFilters,
    parkingLotsEditorFiltersKey,
    parkingLotsMapContextLayerKey,
} from './parkingLotsEditorFilters';

// Use environment configuration for API base URL
const API_BASE = environment.apiUrl;
const resolveApiUrl = (path: string): string => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    return environment.useSameOriginApi ? path : `${API_BASE}${path}`;
};

// Public API v1 (cities, parking-lots list/map, parking-lot by id)
const PUBLIC_API_BASE = environment.useSameOriginApi ? '/api/v1' : `${API_BASE}/api/v1`;

const DEVICE_ID_KEY = 'freespot-device-id';

/** Stable device/session id for public API X-Device-Id header (rate limiting). */
export function getDeviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
        id = 'web-' + crypto.randomUUID();
        localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
}

/** Fetch from public API v1 with required X-Device-Id header. */
async function publicApiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = path.startsWith('http') ? path : `${PUBLIC_API_BASE}${path}`;
    const headers = new Headers(options.headers || {});
    headers.set('X-Device-Id', getDeviceId());
    headers.set('Content-Type', 'application/json');
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        let message = text;
        try {
            const json = JSON.parse(text);
            if (json?.message) message = json.message;
            else if (json?.error) message = json.error;
        } catch {
            // use text as-is
        }
        throw new Error(message || `Request failed: ${res.status}`);
    }
    return res.json();
}

// Production logging (only in development)
if (environment.isDevelopment) {
    console.log('🔧 API Configuration:');
    console.log('  API_BASE:', API_BASE);
    console.log('  Backend Origin:', environment.backendOrigin);
    console.log('  Environment:', environment.environment);
    console.log('  App Name:', environment.appName);
    console.log('  useSameOriginApi:', environment.useSameOriginApi);
}

// Get JWT token from localStorage (set after login)
export const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
};

// The signed-in admin-panel user, as stored at login. Never throws on a
// corrupt/absent entry — callers only ever read `role` off it.
export const getStoredUser = (): { role?: string } | null => {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const isSessionViewer = (): boolean => getStoredUser()?.role === 'session_viewer';

/**
 * Navigate on an auth failure — but NEVER to the page we are already on.
 *
 * Without the guard this is an infinite hard-reload loop: a session_viewer sits
 * on /sessions, a component there requests an admin-only endpoint, the 403
 * handler sends them to /sessions, the full page reload remounts that same
 * component, and it 403s again. Observed in production at ~3 reloads/second,
 * which made the panel unusable for exactly the scoped accounts BPM will use.
 */
const redirectOnAuthFailure = (path: string): void => {
    if (window.location.pathname === path) return;
    window.location.href = path;
};

// API fetch wrapper with auth headers
export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = getAuthToken();
    const headers = new Headers(options.headers || {});
    headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const url = resolveApiUrl(path);
    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        redirectOnAuthFailure('/');
        throw new Error('Authentication required');
    }

    if (res.status === 403) {
        // Access forbidden - redirect to sessions page for session_viewer users
        if (isSessionViewer()) {
            // Send them to the one page they can use — unless they are already
            // there, in which case navigating would just reload into this same 403.
            redirectOnAuthFailure('/sessions');
            throw new Error('Access denied. You only have access to Sessions and Reports pages.');
        } else {
            // For admin users getting 403, still show error
            throw new Error(`Access forbidden: ${res.status} - You do not have permission to access this resource`);
        }
    }

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Request failed: ${res.status}`);
    }
    return res.json();
}

// SWR fetcher with auth headers and mock data fallback
const fetcher = (url: string) => {
    const token = getAuthToken();
    const fullUrl = resolveApiUrl(url);

    // Debug logging for API calls (only in development)
    if (environment.isDevelopment) {
        console.log(`[${environment.environment.toUpperCase()}] 🌐 API Call: ${fullUrl} (${environment.useSameOriginApi ? 'same-origin' : 'direct'})`);
        console.log(`[${environment.environment.toUpperCase()}] 🔑 Token present: ${!!token}`);
    }

    return fetch(fullUrl, {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
    }).then(res => {
        if (res.status === 401) {
            // Token is invalid or expired, redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            redirectOnAuthFailure('/');
            throw new Error('Authentication required');
        }

        if (res.status === 403) {
            // Access forbidden - redirect to sessions page for session_viewer users
            if (isSessionViewer()) {
                // Send them to the one page they can use — unless they are already
                // there, in which case navigating would just reload into this same 403.
                redirectOnAuthFailure('/sessions');
                throw new Error('Access denied. You only have access to Sessions and Reports pages.');
            } else {
                // For admin users getting 403, still redirect but show error
                throw new Error(`[${environment.environment.toUpperCase()}] Access forbidden: ${res.status} - You do not have permission to access this resource`);
            }
        }

        if (!res.ok) {
            // For 500 errors, provide better error messages
            if (res.status === 500) {
                throw new Error(`[${environment.environment.toUpperCase()}] Server error: ${res.status} - Please check if the backend is running`);
            }
            throw new Error(`[${environment.environment.toUpperCase()}] API call failed: ${res.status} ${res.statusText}`);
        }
        return res.json();
    });
};

// Custom fetcher for endpoints that might not be implemented
const fetcherWithMockFallback = (url: string, mockData: any) => {
    const token = getAuthToken();
    const fullUrl = resolveApiUrl(url);

    return fetch(fullUrl, {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
    }).then(res => {
        if (res.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            redirectOnAuthFailure('/');
            throw new Error('Authentication required');
        }

        if (res.status === 403) {
            // Access forbidden - redirect to sessions page for session_viewer users
            if (isSessionViewer()) {
                // Send them to the one page they can use — unless they are already
                // there, in which case navigating would just reload into this same 403.
                redirectOnAuthFailure('/sessions');
                throw new Error('Access denied. You only have access to Sessions and Reports pages.');
            } else {
                throw new Error(`Access forbidden: ${res.status} - You do not have permission to access this resource`);
            }
        }

        if (!res.ok) {
            // Return mock data for 404/500 errors in development only
            if (environment.isDevelopment && (res.status === 404 || res.status === 500)) {
                console.warn(`Endpoint ${url} returned ${res.status}, using mock data (development mode)`);
                return mockData;
            }
            throw new Error(`API call failed: ${res.status} ${res.statusText}`);
        }
        return res.json();
    });
};

export interface ParkingLotsResponse {
    parkingLots: ParkingLot[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

// ---------- Public API v1 (cities, parking-lots list/map, parking-lot by id) ----------

export async function getCities(): Promise<PublicApiCity[]> {
    const res = await publicApiFetch<PublicApiCitiesResponse>('/cities');
    if (!res.success || !Array.isArray(res.data)) return [];
    return res.data;
}

export interface PlaceSearchSuggestion {
    description: string;
    placeId: string;
}

export interface PlaceSearchLocation {
    lat: number;
    lng: number;
    formattedAddress?: string;
}

export function createPlaceSearchSessionToken(): string {
    try {
        if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch {
        // Fall through to a compatibility token for older browsers.
    }
    return `places-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export async function autocompletePlaces(
    query: string,
    biasCity: string,
    sessionToken: string,
    signal?: AbortSignal
): Promise<PlaceSearchSuggestion[]> {
    const params = new URLSearchParams({ query, lang: 'da', sessionToken });
    if (biasCity) params.set('biasCity', biasCity);
    const response = await apiFetch<{ suggestions?: Array<Partial<PlaceSearchSuggestion>> }>(
        `/api/v2/places/autocomplete?${params}`,
        { signal }
    );
    return (response.suggestions ?? []).flatMap((suggestion) => {
        const description = suggestion.description?.trim();
        const placeId = suggestion.placeId?.trim();
        return description && placeId ? [{ description, placeId }] : [];
    });
}

export async function getPlaceSearchLocation(
    placeId: string,
    sessionToken: string,
    signal?: AbortSignal
): Promise<PlaceSearchLocation> {
    const params = new URLSearchParams();
    if (sessionToken) params.set('sessionToken', sessionToken);
    const suffix = params.toString() ? `?${params}` : '';
    const response = await apiFetch<{
        formattedAddress?: string;
        location?: { lat?: number; lng?: number };
    }>(`/api/v2/places/details/${encodeURIComponent(placeId)}${suffix}`, { signal });
    const lat = response.location?.lat;
    const lng = response.location?.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        throw new Error('Stedet har ingen gyldige koordinater');
    }
    return { lat, lng, formattedAddress: response.formattedAddress };
}

export async function getParkingLotsList(city: string, limit: number, offset: number): Promise<PublicApiParkingLotsResponse> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (city && city !== 'all') params.set('city', city);
    const res = await publicApiFetch<PublicApiParkingLotsResponse>(`/parking-lots?${params}`);
    return res;
}

/** Legacy (non-v1) list endpoint for admin list view. Uses auth, returns same shape as v1 for usePublicParkingLotsList. */
export async function getParkingLotsListLegacy(
    city: string,
    page: number,
    pageSize: number,
    search = '',
    sortBy = 'name',
    sortOrder: 'asc' | 'desc' = 'asc',
    filters?: ParkingLotsEditorFiltersState
): Promise<PublicApiParkingLotsResponse> {
    const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sortBy,
        sortOrder
    });
    if (city && city !== 'all') params.set('city', city);
    if (search) params.set('search', search);
    if (filters) appendEditorFiltersToSearchParams(params, filters);
    const res = await apiFetch<ParkingLotsResponse>(`/api/parking-lots?${params}`);
    return {
        success: true,
        data: res.parkingLots,
        meta: {
            total: res.pagination.total,
            limit: res.pagination.limit,
            offset: (page - 1) * pageSize,
        },
    };
}

export async function getParkingLotsMapView(
    city: string,
    bbox?: string,
    zoom?: number,
    filters?: ParkingLotsEditorFiltersState,
    limit?: number
): Promise<PublicApiParkingLotsResponse> {
    const params = new URLSearchParams({ view: 'map' });
    if (city && city !== 'all') params.set('city', city);
    if (bbox) params.set('bbox', bbox);
    if (zoom != null) params.set('zoom', String(zoom));
    if (limit != null) params.set('limit', String(limit));
    if (filters) appendEditorFiltersToSearchParams(params, filters);
    const res = await publicApiFetch<PublicApiParkingLotsResponse>(`/parking-lots?${params}`);
    return res;
}

export async function getParkingLotById(id: string): Promise<ParkingLot> {
    const res = await publicApiFetch<PublicApiParkingLotDetailResponse>(`/parking-lots/${encodeURIComponent(id)}`);
    if (!res.success || !res.data) throw new Error('Parking lot not found');
    const raw = res.data as unknown as Record<string, unknown>;
    const lot = { ...raw } as unknown as ParkingLot;
    if (raw.g != null && raw.geometry == null) lot.geometry = raw.g as ParkingLot['geometry'];
    if (raw.pt != null && raw.parking_type == null) {
        const ptMap: Record<string, string> = { f: 'free', p: 'paid', lt: 'limited_time' };
        lot.parking_type = ptMap[raw.pt as string] ?? (raw.pt as string);
    }
    if (!lot.feedback) lot.feedback = [];
    return lot;
}

/** Legacy (non-v1) single lot for map sidebar: full doc with auth, for edit/delete. */
export async function getParkingLotByIdLegacy(id: string): Promise<ParkingLot> {
    return apiFetch<ParkingLot>(`/api/parking-lots/${encodeURIComponent(id)}`);
}

export function useCities() {
    const { data, error, mutate } = useSWR<PublicApiCity[]>(
        'public-api/cities',
        () => getCities()
    );
    return {
        cities: data ?? [],
        isLoading: !error && data === undefined,
        error: error ?? null,
        mutate,
    };
}

/** Public API map view: minimal payload (id, g, pt, ld). Returns normalized list for map drawing + meta. */
export interface ParkingLotsMapViewport {
    bbox?: string;
    zoom?: number;
    enabled?: boolean;
}

export function useParkingLotsMapView(
    city: string,
    filters?: ParkingLotsEditorFiltersState,
    viewport?: ParkingLotsMapViewport
) {
    const fk = filters ? parkingLotsEditorFiltersKey(filters) : 'default';
    const viewportKey = viewport
        ? `${viewport.bbox || 'no-bbox'}/${viewport.zoom ?? 'no-zoom'}`
        : 'all-data';
    const key = viewport?.enabled === false
        ? null
        : city
            ? `public-api/parking-lots-map/${city}/${fk}/${viewportKey}`
            : `public-api/parking-lots-map/all/${fk}/${viewportKey}`;
    const { data, error, mutate, isValidating } = useSWR<PublicApiParkingLotsResponse>(
        key,
        key
            ? () => getParkingLotsMapView(
                city === 'all' ? '' : city,
                viewport?.bbox,
                viewport?.zoom,
                filters,
                viewport?.bbox ? 10000 : undefined
            )
            : null,
        { keepPreviousData: true }
    );
    const rawData = (key && data?.data && Array.isArray(data.data) ? data.data : []) as ParkingLotMapItem[];
    return {
        rawMapItems: rawData,
        meta: key ? data?.meta : undefined,
        version: key ? data?.version : undefined,
        isLoading: key !== null && !error && data === undefined,
        isValidating: key !== null && isValidating,
        error: error ?? null,
        mutate,
    };
}

/**
 * Secondary map fetch for editor “context” layer (muted polygons).
 * Skips when mode is `none`, or when secondary query would match the primary query (no extra lots).
 */
export function useParkingLotsMapContextLayer(
    city: string,
    primaryFilters: ParkingLotsEditorFiltersState | undefined,
    viewport?: ParkingLotsMapViewport
) {
    const mode = primaryFilters?.mapContextMode ?? 'none';
    const secondary = mapContextSecondaryFilters(mode);
    const secondaryFk = secondary ? parkingLotsEditorFiltersKey(secondary) : '';
    const primaryFk = primaryFilters ? parkingLotsEditorFiltersKey(primaryFilters) : '';
    const redundantLayer =
        secondary != null && primaryFilters != null && primaryFk === secondaryFk;

    const shouldFetch = secondary != null && !redundantLayer && viewport?.enabled !== false;
    const layerSegment = secondary ? parkingLotsMapContextLayerKey(mode, secondaryFk) : 'off';
    const viewportSegment = viewport
        ? `${viewport.bbox || 'no-bbox'}/${viewport.zoom ?? 'no-zoom'}`
        : 'all-data';
    const key = shouldFetch
        ? city
            ? `public-api/parking-lots-map-ctx/${city}/${layerSegment}/${viewportSegment}`
            : `public-api/parking-lots-map-ctx/all/${layerSegment}/${viewportSegment}`
        : null;

    const { data, error, mutate, isValidating } = useSWR<PublicApiParkingLotsResponse>(
        key,
        key && secondary
            ? () => getParkingLotsMapView(
                city === 'all' ? '' : city,
                viewport?.bbox,
                viewport?.zoom,
                secondary,
                viewport?.bbox ? 10000 : undefined
            )
            : null,
        { keepPreviousData: true }
    );
    const rawContextItems = (shouldFetch && data?.data && Array.isArray(data.data) ? data.data : []) as ParkingLotMapItem[];
    return {
        rawContextItems,
        isLoading: shouldFetch && !error && data === undefined,
        isValidating: shouldFetch && isValidating,
        error: error ?? null,
        mutate,
    };
}

/** Public API list view: full payload, paginated by limit/offset. Set enabled=false when not showing list (e.g. on map view). */
export function usePublicParkingLotsList(
    city: string,
    page: number,
    pageSize: number,
    search = '',
    sortBy = 'name',
    sortOrder: 'asc' | 'desc' = 'asc',
    enabled = true,
    filters?: ParkingLotsEditorFiltersState
) {
    const fk = filters ? parkingLotsEditorFiltersKey(filters) : 'default';
    const key = enabled
        ? `public-api/parking-lots-list/${city || 'all'}/${page}/${pageSize}/${search}/${sortBy}/${sortOrder}/${fk}`
        : null;
    const { data, error, mutate } = useSWR<PublicApiParkingLotsResponse>(
        key,
        key ? () => getParkingLotsListLegacy(city === 'all' ? '' : city, page, pageSize, search, sortBy, sortOrder, filters) : null
    );
    const parkingLots = (data?.data && Array.isArray(data.data) ? data.data : []) as ParkingLot[];
    const meta = data?.meta;
    const total = meta?.total ?? 0;
    return {
        parkingLots,
        pagination: {
            page,
            limit: pageSize,
            total,
            pages: pageSize > 0 ? Math.ceil(total / pageSize) || 1 : 1,
        },
        isLoading: !error && data === undefined,
        error: error ?? null,
        mutate,
    };
}

export function useActionHeatmapData(
    action: ActionHeatmapAction,
    city: string,
    startDate: string,
    endDate: string,
    metric: 'total_actions' = 'total_actions'
) {
    const params = new URLSearchParams({
        action,
        city,
        startDate,
        endDate,
        metric,
    });

    const { data, error, mutate } = useSWR<ActionHeatmapResponse>(
        `admin-analytics/map-actions/${params.toString()}`,
        () => apiFetch<ActionHeatmapResponse>(`/api/admin/analytics/map-actions?${params}`)
    );

    return {
        data: data?.data ?? null,
        isLoading: !error && data === undefined,
        error: error as Error | null,
        mutate,
    };
}

export const useParkingLots = (page = 1, limit = 100, search = '', sortBy = 'name', sortOrder: 'asc' | 'desc' = 'asc') => {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
    });

    if (search) {
        params.append('search', search);
    }

    const { data, error, mutate } = useSWR<ParkingLotsResponse>(
        `/api/parking-lots?${params}`,
        (url: string) => {
            // Use the new pagination function for realistic data
            const mockData = generatePaginatedParkingLots(page, limit, search, sortBy, sortOrder);
            return fetcherWithMockFallback(url, mockData);
        }
    );

    return {
        parkingLots: data?.parkingLots || (environment.isDevelopment ? generatePaginatedParkingLots(page, limit, search, sortBy, sortOrder).parkingLots : []),
        pagination: data?.pagination || (environment.isDevelopment ? generatePaginatedParkingLots(page, limit, search, sortBy, sortOrder).pagination : { page: 1, limit, total: 0, pages: 0 }),
        isLoading: !error && !data,
        error,
        mutate,
        isMockData: !data && error && environment.isDevelopment
    };
};

export const useParkingLotStats = () => {
    const { data, error } = useSWR<ParkingLotStats>(
        `/api/parking-lots/stats/summary`,
        (url: string) => fetcherWithMockFallback(url, mockParkingLotStats)
    );

    return {
        stats: data || (environment.isDevelopment ? mockParkingLotStats : null),
        isLoading: !error && !data,
        error,
        isMockData: !data && error && environment.isDevelopment
    };
};

// Utility function for manual API calls
export const apiCall = async <T>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiFetch<T>(endpoint, options);
};

// Feedback management functions
export const submitFeedback = async (parkingLotId: string, feedback: {
    feedbackType: 'up' | 'down' | 'comment';
    comment?: string;
    userId?: string;
    searchId?: string;
    spotAddress?: string;
    spotType?: string;
}): Promise<ParkingLot> => {
    return apiFetch<ParkingLot>(`/api/parking-lots/${parkingLotId}/feedback`, {
        method: 'POST',
        body: JSON.stringify(feedback),
    });
};

export const deleteFeedback = async (parkingLotId: string, feedbackId: string) => {
    return apiFetch(`/api/parking-lots/${parkingLotId}/feedback/${feedbackId}`, {
        method: 'DELETE',
    });
};

// Parking lot CRUD operations
export const createParkingLot = async (parkingLotData: Partial<ParkingLot>): Promise<ParkingLot> => {
    return apiFetch<ParkingLot>('/api/parking-lots', {
        method: 'POST',
        body: JSON.stringify(parkingLotData),
    });
};

export const updateParkingLot = async (id: string, parkingLotData: Partial<ParkingLot>): Promise<ParkingLot> => {
    return apiFetch<ParkingLot>(`/api/parking-lots/${id}`, {
        method: 'PUT',
        body: JSON.stringify(parkingLotData),
    });
};

export const deleteParkingLot = async (id: string): Promise<void> => {
    return apiFetch<void>(`/api/parking-lots/${id}`, {
        method: 'DELETE',
    });
};

export const archiveDeleteParkingLot = async (
    id: string,
    reason?: string
): Promise<{ archivedLotId: string }> => {
    return apiFetch<{ success: boolean; archivedLotId: string }>(
        `/api/admin/parking-lots/${encodeURIComponent(id)}/archive-delete`,
        {
            method: 'POST',
            body: JSON.stringify({ reason: reason || null }),
        }
    );
};

// --- Public map feedback (stored on marketing site MongoDB; admin JWT or FEEDBACK_ADMIN_API_KEY) ---
export interface PublicParkingLotFeedbackItem {
    id: string;
    lotId: string;
    parentLotId: string | null;
    status: string;
    submittedAt: string;
    baseGeometryHash: string;
    kind: 'edit' | 'new';
    proposal: {
        geometry: import('./types').Geometry;
        name?: string;
        address?: string;
        city?: string;
        comment?: string | null;
        priceZone?: string | null;
        paidHours?: string | null;
        spaces?: string | number | null;
        additionalProperties?: {
            free_duration_rules?: string | null;
            cleaned_price_zone?: string | null;
            vehicle_restriction?: string | null;
        } | null;
        parking_type?: string | null;
        restriction_description?: string | null;
    };
}

export async function fetchPublicParkingLotFeedbackProposals(
    status = 'pending',
    city = ''
): Promise<PublicParkingLotFeedbackItem[]> {
    const params = new URLSearchParams({ status });
    if (city && city !== 'all') params.set('city', city);
    const res = await apiFetch<{ success: boolean; data: PublicParkingLotFeedbackItem[] }>(
        `/api/admin/public-feedback?${params.toString()}`
    );
    return res.data || [];
}

export async function patchPublicParkingLotFeedbackProposal(
    id: string,
    action: 'accepted' | 'rejected'
): Promise<void> {
    await apiFetch(`/api/admin/public-feedback/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ action }),
    });
}

export async function acceptPublicParkingLotFeedbackProposal(
    id: string,
    final: {
        geometry: import('./types').Geometry;
        name?: string | null;
        address?: string | null;
        city?: string | null;
        parking_type?: string | null;
        spaces?: string | number | null;
        additionalProperties?: {
            free_duration_rules?: string | null;
            cleaned_price_zone?: string | null;
            paid_hours?: string | null;
            vehicle_restriction?: string | null;
        } | null;
        restriction_description?: string | null;
    }
): Promise<{ acceptedLotId: string; replacedLotId: string | null }> {
    const res = await apiFetch<{ success: boolean; acceptedLotId: string; replacedLotId: string | null }>(
        `/api/admin/public-feedback/${encodeURIComponent(id)}/accept`,
        {
            method: 'POST',
            body: JSON.stringify({ final }),
        }
    );
    return { acceptedLotId: res.acceptedLotId, replacedLotId: res.replacedLotId };
}

// GeoJSON Import
export interface GeoJSONImportResponse {
    success: boolean;
    created: number;
    errors: number; // Count of errors
    createdLots: Array<{
        lotId: string;
        name: string;
    }>;
    errorDetails?: Array<{
        index: number;
        error: string;
        lotId?: string;
    }>;
}

export const importGeoJSON = async (geoJson: any): Promise<GeoJSONImportResponse> => {
    return apiFetch<GeoJSONImportResponse>('/api/parking-lots/import-geojson', {
        method: 'POST',
        body: JSON.stringify(geoJson),
    });
};

// Single parking lot hook
export const useParkingLot = (id: string) => {
    const { data, error, mutate } = useSWR<ParkingLot>(
        id ? `/api/parking-lots/${id}` : null,
        (url: string) => fetcherWithMockFallback(url, mockParkingLots.find(lot => lot._id === id))
    );

    return {
        parkingLot: data || (environment.isDevelopment ? mockParkingLots.find(lot => lot._id === id) : null),
        isLoading: !error && !data,
        error,
        mutate,
        isMockData: !data && error && environment.isDevelopment
    };
};

export const useDashboardOverview = () => {
    const { data, error, mutate } = useSWR<DashboardOverview>(
        `/api/analytics/dashboard`,
        (url: string) => fetcherWithMockFallback(url, mockDashboardData)
    );

    return {
        dashboardData: data || (environment.isDevelopment ? mockDashboardData : null),
        isLoading: !error && !data,
        error: error as Error | null,
        mutate,
        isMockData: !data && error && environment.isDevelopment
    };
};

// ===========================================================================
// Analytics Panel Hooks (/api/analytics/panel/*) — the Mixpanel-style hub.
// All accept an optional { from, to } ISO date range.
// ===========================================================================

const PANEL_SWR_OPTS = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 30000,
};

const rangeQuery = (range?: { from?: string; to?: string }): string => {
    const p = new URLSearchParams();
    if (range?.from) p.set('from', range.from);
    if (range?.to) p.set('to', range.to);
    return p.toString();
};

export const usePanelOverview = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<OverviewResponse>(
        `/api/analytics/panel/overview?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelNewUsers = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<NewUsersBreakdownResponse>(
        `/api/analytics/panel/new-users?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelTimeseries = (
    metric: TimeseriesMetric,
    range?: { from?: string; to?: string },
    cumulative = false
) => {
    const params = new URLSearchParams(rangeQuery(range));
    params.set('metric', metric);
    // Running total from the start of the range (distinct users for active_users).
    if (cumulative) params.set('cumulative', '1');
    // Same dev-only test-traffic override as usePanelReminders (only the
    // reminders_sent metric honors it backend-side).
    if (metric === 'reminders_sent' && environment.isDevelopment) {
        params.set('includeTest', '1');
    }
    const { data, error, mutate } = useSWR<TimeseriesResponse>(
        `/api/analytics/panel/timeseries?${params.toString()}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelPaymentFunnel = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<PaymentFunnelResponse>(
        `/api/analytics/panel/payment-funnel?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelSearchFunnel = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<SearchFunnelResponse>(
        `/api/analytics/panel/search-funnel?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelReminders = (range?: { from?: string; to?: string }) => {
    // Dev/staging admin keeps is_test traffic visible — reminder testing runs
    // on dev builds, whose events are all flagged is_test. Prod never sends it.
    const includeTest = environment.isDevelopment ? '&includeTest=1' : '';
    const { data, error, mutate } = useSWR<RemindersResponse>(
        `/api/analytics/panel/reminders?${rangeQuery(range)}${includeTest}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelTopLots = (
    range?: { from?: string; to?: string },
    limit = 15,
    source?: TopLotsSource,
    city?: string, // canonical city slug ('aalborg'|'aarhus'|'koebenhavn'); omit for all cities
) => {
    const params = new URLSearchParams(rangeQuery(range));
    params.set('limit', String(limit));
    if (source) params.set('source', source);
    if (city) params.set('city', city);
    const { data, error, mutate } = useSWR<TopLotsResponse>(
        `/api/analytics/panel/top-lots?${params.toString()}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const usePanelEvents = (params: {
    from?: string;
    to?: string;
    event_name?: string;
    user?: string;
    source?: PanelEventSource;
    page?: number;
    limit?: number;
}) => {
    const q = new URLSearchParams(rangeQuery(params));
    if (params.event_name) q.set('event_name', params.event_name);
    if (params.user) q.set('user', params.user);
    if (params.source) q.set('source', params.source);
    q.set('page', String(params.page || 1));
    q.set('limit', String(params.limit || 50));
    const { data, error, mutate, isValidating } = useSWR<PanelEventsResponse>(
        `/api/analytics/panel/events?${q.toString()}`,
        fetcher,
        {
            ...PANEL_SWR_OPTS,
            revalidateOnReconnect: true,
            keepPreviousData: true,
        }
    );
    return { data: data ?? null, isLoading: !error && !data, isValidating, error: error as Error | null, mutate };
};

export const usePanelEventNames = (
    range?: { from?: string; to?: string },
    source?: PanelEventSource,
) => {
    const q = new URLSearchParams(rangeQuery(range));
    if (source) q.set('source', source);
    const { data } = useSWR<EventNamesResponse>(
        `/api/analytics/panel/event-names?${q.toString()}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { names: data?.names ?? [] };
};

// --- Website analytics (marketing site) — /api/analytics/panel/web-* ---

export const useWebOverview = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<WebOverviewResponse>(
        `/api/analytics/panel/web-overview?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const useWebAcquisition = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<WebAcquisitionResponse>(
        `/api/analytics/panel/web-acquisition?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const useWebCityEngagement = (range?: { from?: string; to?: string }) => {
    const { data, error, mutate } = useSWR<WebCityEngagementResponse>(
        `/api/analytics/panel/web-city-engagement?${rangeQuery(range)}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

export const useWebTimeseries = (
    metric: WebTimeseriesMetric,
    range?: { from?: string; to?: string },
    cumulative = false
) => {
    const params = new URLSearchParams(rangeQuery(range));
    params.set('metric', metric);
    // Running total from the start of the range (distinct visitors for visitors).
    if (cumulative) params.set('cumulative', '1');
    const { data, error, mutate } = useSWR<WebTimeseriesResponse>(
        `/api/analytics/panel/web-timeseries?${params.toString()}`,
        fetcher,
        PANEL_SWR_OPTS
    );
    return { data: data ?? null, isLoading: !error && !data, error: error as Error | null, mutate };
};

// Feedback Analytics Hooks (use shared fetcher — no silent mock fallback on API errors)
export const useFeedbackSummary = () => {
    const { data, error, mutate } = useSWR<FeedbackSummary>(
        `/api/analytics/feedback/summary`,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            dedupingInterval: 30000
        }
    );

    return {
        summary: data ?? null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const useFeedbackActivity = (page: number = 1, limit: number = 50, filters: {
    feedbackType?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
} = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(filters.feedbackType && { feedbackType: filters.feedbackType }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
    });

    const { data, error, mutate } = useSWR<FeedbackActivityResponse>(
        `/api/analytics/feedback/activity?${params}`,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            dedupingInterval: 30000
        }
    );

    return {
        activity: data ?? null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

// Live Data Controls API Functions
export const useLiveDataControls = () => {
    const { data, error, mutate } = useSWR<LiveDataControlsResponse>(
        `/api/v1/lots/admin/live-data-controls`,
        async (url: string) => {
            try {
                const token = getAuthToken();
                const response = await fetch(resolveApiUrl(url), {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`API call failed: ${response.status} ${response.statusText}`);
                }

                return await response.json();
            } catch (err) {
                console.error('Live data controls fetch failed:', err);
                throw err;
            }
        },
        {
            refreshInterval: 30000, // Auto-refresh every 30 seconds
            revalidateOnFocus: true,
            revalidateOnReconnect: true
        }
    );

    return {
        controls: data?.data?.controls || [],
        summary: data?.data?.summary || { total: 0, enabled: 0, disabled: 0 },
        isLoading: !error && !data,
        error: error as Error | null,
        mutate,
        lastUpdated: data?.timestamp
    };
};

export const toggleLotStatus = async (lotId: string): Promise<ToggleLotResponse> => {
    const token = getAuthToken();
    const response = await fetch(resolveApiUrl(`/api/v1/lots/admin/live-data-controls/${lotId}/toggle`), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Toggle failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
};

export const updateMetrics = async (): Promise<UpdateMetricsResponse> => {
    const token = getAuthToken();
    const response = await fetch(resolveApiUrl('/api/v1/lots/admin/live-data-metrics/update'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Update metrics failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
};

// Version Control API Functions
export const useVersionInfo = () => {
    const { data, error, mutate } = useSWR<VersionResponse>(
        `/api/v1/parking-lots/version`,
        async (url: string) => {
            try {
                const token = getAuthToken();
                const response = await fetch(resolveApiUrl(url), {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`API call failed: ${response.status} ${response.statusText}`);
                }

                return await response.json();
            } catch (err) {
                console.error('Version info fetch failed:', err);
                throw err;
            }
        },
        {
            refreshInterval: 60000, // Refresh every minute
            revalidateOnFocus: true,
            revalidateOnReconnect: true
        }
    );

    return {
        versionInfo: data?.data || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate,
        lastUpdated: data ? new Date().toISOString() : null
    };
};

export const forceVersionUpdate = async (): Promise<ForceVersionUpdateResponse> => {
    const token = getAuthToken();
    const response = await fetch(resolveApiUrl('/api/admin/bump-version'), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Force version update failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
};

// Sessions API Functions
export interface SessionsFilters {
    status?: string;
    startDate?: string;
    endDate?: string;
    areaNo?: string; // Matches Aalborg area numbers or Copenhagen PRS zones
    licensePlate?: string;
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    summary?: boolean;
    includeHidden?: boolean;
}

export const useSessions = (filters: SessionsFilters = {}) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
        }
    });

    const { data, error, mutate } = useSWR<SessionsResponse>(
        `/api/admin/sessions?${params}`,
        fetcher
    );

    return {
        sessions: data?.data?.sessions || [],
        pagination: data?.data?.pagination || null,
        summary: data?.data?.summary || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

// Access scope — "what may this account see?"
//
// The Reports page and the Sessions columns render from this instead of
// hardcoding every municipality's section and showing them to everyone. It is a
// DRAWING INSTRUCTION, not a permission: editing this response in a browser adds
// a card to the screen and nothing else, because the settlement endpoints still
// run their own city gate and the queries are still scoped in the database.
//
// For an admin the city list is every city, which is also exactly the set
// assignable to a new viewer account — so the Team page picker reads it too.
export type CityReporting = 'aalborg-monthly' | 'kk-settlement' | null;

export interface ScopeCity {
    id: string;
    name: string;
    /** The municipality as named on the contract, e.g. "Københavns Kommune". */
    settlementName: string;
    reporting: CityReporting;
    /** How this city's sessions are identified. null = unknown, show both. */
    sessionIdentifier: 'areaNo' | 'zone' | null;
    /**
     * Older spellings of this city's id that still scope sessions but fail the
     * exact-match gate on its settlement reports. A grant stored as an alias
     * needs re-saving; it is not broken, but it is not right either.
     */
    aliases: string[];
}

export interface AdminScope {
    role: string;
    unrestricted: boolean;
    cities: ScopeCity[];
    areaNos: string[];
    /** Grants on this account that match no known city — surfaced, not hidden. */
    unknownCities: string[];
}

export const useAdminScope = () => {
    const { data, error, mutate } = useSWR<{ success: boolean; data: AdminScope }>(
        '/api/admin/me/scope',
        fetcher
    );

    return {
        scope: data?.data || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

// Reports API Functions
export interface ReportParams {
    year?: number;
    month?: number;
    week?: number;
    day?: number;
    startTime?: string;
    endTime?: string;
    format?: 'json' | 'csv' | 'excel' | 'xlsx';
}

export const fetchReport = async (params: ReportParams): Promise<ReportResponse | Blob> => {
    const token = getAuthToken();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]: [string, any]) => {
        if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
        }
    });

    const url = resolveApiUrl(`/api/admin/reports/monthly?${queryParams}`);

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Report fetch failed: ${response.status} ${response.statusText}`);
    }

    // If format is csv or excel, return blob
    if (params.format === 'csv' || params.format === 'excel' || params.format === 'xlsx') {
        return response.blob();
    }

    return response.json();
};

export const downloadReport = async (format: 'csv' | 'excel', params: ReportParams) => {
    const blob = await fetchReport({ ...params, format: format === 'excel' ? 'xlsx' : 'csv' }) as Blob;
    
    // Extract filename from Content-Disposition header if available, or generate one
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Generate filename based on date params
    let filename = 'betalingsrapport';
    if (params.year && params.month) {
        filename += `-${params.year}-${String(params.month).padStart(2, '0')}`;
    } else if (params.startTime && params.endTime) {
        const start = new Date(params.startTime).toISOString().split('T')[0];
        const end = new Date(params.endTime).toISOString().split('T')[0];
        filename += `-${start}-${end}`;
    } else {
        filename += `-${new Date().toISOString().split('T')[0]}`;
    }
    
    a.download = `${filename}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

// KBH / Københavns Kommune monthly settlement (KBH launch plan §9 P8)
export interface KkReconciliation {
    transactions: number;
    amountToKK: number;            // = REP1/REP2 total = the bank transfer to KK
    serviceFeeRetained: number;    // ours, NOT sent to KK
    dbTotal: number;
    nexiCapturedNet: number;       // Nexi captured minus refunds
    expectedChargedFromPricing: number;
    missingCapturedAmountCount?: number;
    missingCapturedAmountTotal?: number;
    captureDataComplete?: boolean;
    nexiMatches: boolean;          // nexiCapturedNet === parking + service
    bankAccount: { reg: string; konto: string };
}

export interface ReportQualityIssue {
    sessionId?: string; // absent for 'insights' accounts (codes only)
    code: string;
    values?: string[];
}

export interface ReportQuality {
    profileId: string;
    candidateSessions: number;
    includedSessions: number;
    readyForClose: boolean;
    exceptions: ReportQualityIssue[];
    warnings: ReportQualityIssue[];
}

export interface KkReconciliationResponse {
    success: boolean;
    period: { year: number; month: number };
    sessionCount: number;
    reconciliation: KkReconciliation;
    quality: ReportQuality;
}

export const fetchKkReconciliation = async (year: number, month: number): Promise<KkReconciliationResponse> => {
    const token = getAuthToken();
    const url = resolveApiUrl(`/api/admin/reports/kk/reconciliation?year=${year}&month=${month}`);
    const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
        throw new Error(`KK reconciliation failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
};

export const downloadKkReport = async (kind: 'monthly-zone' | 'transactions', year: number, month: number) => {
    const token = getAuthToken();
    const url = resolveApiUrl(`/api/admin/reports/kk/${kind}?year=${year}&month=${month}`);
    const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        const exceptionCount = body?.quality?.exceptions?.length;
        const detail = exceptionCount
            ? ` (${exceptionCount} unresolved settlement issue${exceptionCount === 1 ? '' : 's'})`
            : '';
        throw new Error(`${body?.error || `KK report download failed: ${response.status} ${response.statusText}`}${detail}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    const base = kind === 'monthly-zone' ? 'kk-maanedsrapport' : 'kk-enkelttransaktioner';
    a.download = `${base}-${year}-${String(month).padStart(2, '0')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(a);
};

// PRS health / ops (KBH launch plan §9). All JSON; admin-only endpoints behind
// the backend's fail-closed admin-key/JWT gate.
export interface PrsOutboxStatus {
    pending: number;
    failed: number;
    oldestPendingAgeMinutes: number;
}

export interface PrsStatus {
    enabled: boolean;
    environment: string;
    outbox: PrsOutboxStatus;
    last24h: { success: number; failure: number; total: number };
}

export interface PrsFlushReport {
    scanned: number;
    resolved: number;
    transientRetained: number;
    permanentFailed: number;
    deferred: number;
    pendingRemaining: number;
    failedTotal: number;
    oldestPendingAgeMs: number;
    durationMs: number;
}

export interface PrsReconciliationReport {
    ranAt: string;
    windowFrom: string;
    windowTo: string;
    environment?: string;
    ok: boolean;
    skipped?: boolean;
    skipReason?: string;
    counts?: {
        ourSessions: number;
        theirRights: number;
        matched: number;
        missingInPrs: number;
        mismatched: number;
        staleUnsynced: number;
        extraInPrs: number;
    };
    missingInPrs?: Array<{ sessionId: string; prid: string | null; validityEnd: string | null }>;
    mismatched?: Array<{ sessionId: string; prid: string | null; ourValidityEnd: string; prsValidityEnd: string }>;
    extraInPrs?: Array<{ transactionId: string; prid: string | null }>;
    staleUnsynced?: Array<{ sessionId: string; lastError: string | null; retries: number }>;
}

export interface PrsDigest {
    generatedAt: string;
    outbox: PrsOutboxStatus | null;
    last24h: { success: number; failure: number; total: number } | null;
    reconciliation: PrsReconciliationReport | null;
    sessions24h: { total: number; synced: number; unsynced: number } | null;
}

export const fetchPrsStatus = async (): Promise<PrsStatus> =>
    apiFetch<PrsStatus>('/api/admin/prs/status');

export const flushPrsOutbox = async (): Promise<{ ok: boolean; report: PrsFlushReport }> =>
    apiFetch('/api/admin/prs/flush', { method: 'POST' });

export const fetchPrsReconciliationLatest = async (): Promise<{ latest: PrsReconciliationReport | null }> =>
    apiFetch('/api/admin/prs/reconciliation/latest');

export const runPrsReconciliation = async (): Promise<{ ok: boolean; report: PrsReconciliationReport }> =>
    apiFetch('/api/admin/prs/reconciliation/run', { method: 'POST' });

export const runPrsDigest = async (): Promise<{ ok: boolean; alertsRaised: number; digest: PrsDigest }> =>
    apiFetch('/api/admin/prs/digest/run', { method: 'POST' });

/**
 * DMR lookup usage against the daily budget. `GET /api/vehicles/lookup/:plate`
 * is public (guests verify plates mid-checkout), so this is the only visibility
 * into whether it is being abused. Sources are salted IP hashes — never
 * addresses — and no plates are recorded.
 */
export interface DmrUsage {
    day: string;
    budget: number;
    total: number;
    topSources: Array<{ ipHash: string; count: number }>;
}

export const fetchDmrUsage = async (day?: string): Promise<DmrUsage> =>
    apiFetch(`/api/admin/prs/dmr-usage${day ? `?day=${encodeURIComponent(day)}` : ''}`);

// Receipt Requests API Functions
export const useReceiptRequests = (filters: ReceiptRequestFilters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);

    const { data, error, mutate } = useSWR<ReceiptRequestsResponse>(
        `/api/admin/receipt-requests?${params}`,
        fetcher
    );

    return {
        requests: data?.data?.requests || [],
        pagination: data?.data?.pagination || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const updateReceiptRequestStatus = async (
    requestId: string,
    status: 'sent' | 'cancelled'
): Promise<ReceiptRequest> => {
    const response = await apiFetch<{ success: boolean; data: ReceiptRequest }>(
        `/api/admin/receipt-requests/${requestId}`,
        {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        }
    );
    return response.data;
};

export const updateBulkReceiptRequestStatus = async (
    requestIds: string[],
    status: 'sent' | 'cancelled'
): Promise<{ updated: number; failed: number }> => {
    const response = await apiFetch<{ success: boolean; data: { updated: number; failed: number } }>(
        `/api/admin/receipt-requests/bulk`,
        {
            method: 'PATCH',
            body: JSON.stringify({ requestIds, status }),
        }
    );
    return response.data;
};

// Campaign Management API Functions
export const useCampaigns = () => {
    // Admin-only endpoint. MarkRefundedModal mounts on the Sessions page, which
    // session_viewers CAN see, so an ungated key fires a guaranteed 403 for them.
    const { data, error, mutate } = useSWR<CampaignsResponse>(
        isSessionViewer() ? null : '/api/admin/campaigns',
        fetcher
    );

    return {
        campaigns: data?.data || [],
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const useActiveCampaign = () => {
    const { data, error, mutate } = useSWR<CampaignResponse>(
        isSessionViewer() ? null : '/api/admin/campaigns/active',
        fetcher
    );

    return {
        campaign: data?.data || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const useCampaign = (campaignId: string | null) => {
    const { data, error, mutate } = useSWR<CampaignResponse>(
        campaignId ? `/api/admin/campaigns/${campaignId}` : null,
        fetcher
    );

    return {
        campaign: data?.data || null,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const useEligibleSessions = (campaignId: string | null, limit: number = 100, offset: number = 0) => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const { data, error, mutate } = useSWR<EligibleSessionsResponse>(
        campaignId ? `/api/admin/campaigns/${campaignId}/eligible-sessions?${params}` : null,
        fetcher
    );

    return {
        sessions: data?.data?.sessions || [],
        total: data?.data?.total || 0,
        isLoading: !error && !data,
        error: error as Error | null,
        mutate
    };
};

export const createCampaign = async (campaignData: Omit<Campaign, '_id' | 'createdAt' | 'updatedAt'>): Promise<Campaign> => {
    const response = await apiFetch<CampaignResponse>(
        '/api/admin/campaigns',
        {
            method: 'POST',
            body: JSON.stringify(campaignData),
        }
    );
    return response.data as Campaign;
};

export const updateCampaign = async (
    campaignId: string,
    updateData: Partial<Omit<Campaign, '_id' | 'campaignId' | 'createdAt' | 'updatedAt'>>
): Promise<Campaign> => {
    const response = await apiFetch<CampaignResponse>(
        `/api/admin/campaigns/${campaignId}`,
        {
            method: 'PATCH',
            body: JSON.stringify(updateData),
        }
    );
    return response.data as Campaign;
};

export const markSessionRefunded = async (
    sessionId: string,
    campaignId: string,
    reference?: string
): Promise<MarkRefundedResponse> => {
    return apiFetch<MarkRefundedResponse>(
        `/api/admin/sessions/${sessionId}/mark-refunded`,
        {
            method: 'POST',
            body: JSON.stringify({ campaignId, reference }),
        }
    );
};

// Export types for use in components
export type { 
    ReceiptRequest, 
    ReceiptRequestFilters,
    Campaign,
    CampaignsResponse,
    CampaignResponse,
    EligibleSessionsResponse,
    MarkRefundedRequest,
    MarkRefundedResponse,
    CampaignRefund
};
